import { Box, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { NumericFormatterTextField } from "@platform/design-system";
import {
  heapedToPair,
  pairToHeaped,
  PEPM_MAX,
  PERCENT_MAX,
} from "@platform/formatting-utils";
import { ERROR_MESSAGES } from "@platform/rfp-core";
import { JSX, useState } from "react";

import {
  FormCommissionDetail,
  GradedMode,
} from "../../coverages/CoverageForms/commissionValidation";

export interface CommissionDetailFieldsProps {
  type: string;
  value?: FormCommissionDetail;
  error?: string;
  testId: string;
  /** Whether the commission this detail belongs to has to be filled in. */
  required?: boolean;
  onChange: (detail: FormCommissionDetail) => void;
  onCommit: (detail: FormCommissionDetail) => void;
  onEnter?: () => void;
}

const toField = (amount?: number | null): string =>
  typeof amount === "number" ? String(amount) : "";

const toAmount = (field: string): number | undefined =>
  field === "" ? undefined : Number(field);

const AmountField = ({
  type,
  value,
  error,
  testId,
  required,
  onChange,
  onCommit,
  onEnter,
}: CommissionDetailFieldsProps): JSX.Element => {
  const isPepm = type === "pepm";
  // PEPM is capped with a message rather than at the keystroke, so the value
  // is allowed above the maximum and flagged while it stays there.
  const exceedsPepmMax =
    isPepm && typeof value?.amount === "number" && value.amount > PEPM_MAX;
  const message = exceedsPepmMax ? ERROR_MESSAGES.COMMISSION_PEPM_MAX : error;

  return (
    <NumericFormatterTextField
      label={isPepm ? "Amount" : "Percentage"}
      required={required}
      value={toField(value?.amount)}
      error={!!message}
      helperText={message}
      onFocus={onEnter}
      onChange={(event): void =>
        onChange({ type, amount: toAmount(event.target.value) })
      }
      onBlur={(): void => onCommit({ type, amount: value?.amount })}
      prefix={isPepm ? "$" : undefined}
      suffix={isPepm ? undefined : "%"}
      decimalScale={2}
      allowNegative={false}
      isAllowed={({ floatValue }): boolean =>
        floatValue === undefined || isPepm || floatValue <= PERCENT_MAX
      }
      fullWidth
      sx={{ mt: 2 }}
      data-testid={`${testId}-${isPepm ? "pepm" : "percentage"}`}
    />
  );
};

const HeapedFields = ({
  type,
  value,
  error,
  testId,
  onChange,
  onCommit,
  onEnter,
}: CommissionDetailFieldsProps): JSX.Element => {
  const heaped = pairToHeaped(value?.heapedAmount) ?? value?.heapedDraft;

  const [first, setFirst] = useState(() => toField(heaped?.first));
  const [thereafter, setThereafter] = useState(() =>
    toField(heaped?.thereafter),
  );

  const firstError = first === "" ? error : undefined;
  const thereafterError = thereafter === "" ? error : undefined;

  const toDetail = (
    nextFirst: string,
    nextThereafter: string,
  ): FormCommissionDetail => {
    const draft = {
      first: toAmount(nextFirst),
      thereafter: toAmount(nextThereafter),
    };
    const heapedAmount = heapedToPair(draft);

    return {
      type,
      heapedAmount,
      ...(heapedAmount ? {} : { heapedDraft: draft }),
    };
  };

  const changeFirst = (next: string): void => {
    setFirst(next);
    onChange(toDetail(next, thereafter));
  };

  const changeThereafter = (next: string): void => {
    setThereafter(next);
    onChange(toDetail(first, next));
  };

  const emitHeaped = (): void => onCommit(toDetail(first, thereafter));

  return (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      <NumericFormatterTextField
        label="First Year"
        required={thereafter !== ""}
        value={first}
        error={!!firstError}
        helperText={firstError}
        onFocus={onEnter}
        onChange={(event): void => changeFirst(event.target.value)}
        onBlur={emitHeaped}
        suffix="%"
        decimalScale={2}
        allowNegative={false}
        isAllowed={({ floatValue }): boolean =>
          floatValue === undefined || floatValue <= PERCENT_MAX
        }
        fullWidth
        data-testid={`${testId}-heaped-first`}
      />
      <NumericFormatterTextField
        label="Thereafter"
        required={first !== ""}
        value={thereafter}
        error={!!thereafterError}
        helperText={thereafterError}
        onFocus={onEnter}
        onChange={(event): void => changeThereafter(event.target.value)}
        onBlur={emitHeaped}
        suffix="%"
        decimalScale={2}
        allowNegative={false}
        isAllowed={({ floatValue }): boolean =>
          floatValue === undefined || floatValue <= PERCENT_MAX
        }
        fullWidth
        data-testid={`${testId}-heaped-thereafter`}
      />
    </Stack>
  );
};

const GradedFields = ({
  type,
  value,
  error,
  testId,
  onChange,
  onCommit,
  onEnter,
}: CommissionDetailFieldsProps): JSX.Element => {
  // Derived on mount only: once the broker picks a mode, that choice is theirs
  // and must survive an amount they have not typed yet.
  const [mode, setMode] = useState<GradedMode | "">(() => {
    if (value?.amount === null) return "none";
    if (typeof value?.amount === "number") return "percent";
    return value?.gradedMode ?? "";
  });

  const isPercentGraded = mode === "percent";
  const highestError = isPercentGraded ? error : undefined;

  const changeMode = (next: GradedMode): void => {
    setMode(next);
    onCommit({
      type,
      gradedMode: next,
      amount: next === "none" ? null : toAmount(toField(value?.amount)),
    });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <RadioGroup
        value={mode}
        onChange={(event): void => changeMode(event.target.value as GradedMode)}
      >
        <FormControlLabel
          value="none"
          control={<Radio data-testid={`${testId}-graded-none`} />}
          label="No defined percentage"
        />
        <FormControlLabel
          value="percent"
          control={<Radio data-testid={`${testId}-graded-percent`} />}
          label="Percent graded"
        />
      </RadioGroup>
      <NumericFormatterTextField
        label="Highest %"
        required={isPercentGraded}
        disabled={!isPercentGraded}
        error={!!highestError}
        helperText={highestError}
        value={toField(value?.amount)}
        onFocus={onEnter}
        onChange={(event): void =>
          onChange({
            type,
            gradedMode: "percent",
            amount: toAmount(event.target.value),
          })
        }
        onBlur={(): void =>
          onCommit({ type, gradedMode: "percent", amount: value?.amount })
        }
        suffix="%"
        decimalScale={2}
        allowNegative={false}
        isAllowed={({ floatValue }): boolean =>
          floatValue === undefined || floatValue <= PERCENT_MAX
        }
        fullWidth
        data-testid={`${testId}-graded-highest`}
      />
    </Box>
  );
};

const FIELDS_BY_TYPE: Record<
  string,
  (props: CommissionDetailFieldsProps) => JSX.Element
> = {
  percentage: AmountField,
  pepm: AmountField,
  heaped: HeapedFields,
  graded: GradedFields,
};

/**
 * The detail a commission type takes, alongside its dropdown.
 *
 * Percentage and PEPM need their number before the step will advance, and so
 * does graded under "Percent graded"; heaped takes both percentages or neither.
 * The percentage and PEPM asterisk only shows on a required commission.
 */
export const CommissionDetailFields = (
  props: CommissionDetailFieldsProps,
): JSX.Element | null => {
  const Fields = FIELDS_BY_TYPE[props.type];

  return Fields ? <Fields {...props} /> : null;
};
